import { inject } from 'vue'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreModals } from '@/stores/storeModals'

export function useGuestActions() {
  const storeAuth = useStoreAuth()
  const storeModals = useStoreModals()

  // Context-aware modal: use card context modal if available, otherwise global
  const cardContextModal = inject('cardContextModal', null)
  const openModal = (params) => {
    if (cardContextModal?.isInCardContext) {
      cardContextModal.openModal(params)
    } else {
      storeModals.openModal(params)
    }
  }

  /**
   * Checks if user is authenticated, and if not, shows appropriate guest action prompt
   * @param {string} actionType - Type of action being attempted (review, addToList, etc.)
   * @param {function} authenticatedCallback - Function to call if user is authenticated
   * @param {object} contextData - Additional context about the action
   * @returns {boolean} - True if action should proceed, false if guest prompt was shown
   */
  const requireAuth = (actionType, authenticatedCallback, contextData = {}) => {
    if (storeAuth.user) {
      // User is authenticated, proceed with action
      if (typeof authenticatedCallback === 'function') {
        return authenticatedCallback()
      }
      return true
    } else {
      // User is not authenticated, show guest prompt
      showGuestActionPrompt(actionType, contextData)
      return false
    }
  }

  /**
   * Shows the guest action prompt modal
   * @param {string} actionType - Type of action being attempted
   * @param {object} contextData - Additional context about the action
   */
  const showGuestActionPrompt = (actionType, contextData = {}) => {
    openModal({
      type: 'GuestActionPrompt',
      props: {
        actionType,
        contextData
      },
      canDismiss: true
    })
  }

  /**
   * Convenience methods for common actions
   */
  const requireAuthFor = {
    review: (callback, titleData = {}) =>
      requireAuth('review', callback, { title: titleData }),

    addToList: (callback, titleData = {}) =>
      requireAuth('addToList', callback, { title: titleData }),

    markWatched: (callback, titleData = {}) =>
      requireAuth('markWatched', callback, { title: titleData }),

    follow: (callback, userData = {}) =>
      requireAuth('follow', callback, { user: userData }),

    createList: (callback) => requireAuth('createList', callback),

    favorite: (callback, titleData = {}) =>
      requireAuth('favorite', callback, { title: titleData }),

    message: (callback, contextData = {}) =>
      requireAuth('message', callback, contextData)
  }

  return {
    requireAuth,
    showGuestActionPrompt,
    requireAuthFor,
    isAuthenticated: () => !!storeAuth.user
  }
}
